/**
 * 登记页提示文案加载器
 * 从 /config/registration-tips.json 加载，失败时使用默认文案，不影响主流程
 */

export const DEFAULT_REGISTRATION_TIPS = {
  textFileNameTip:
    '注意：作品名称必须和样本的标题一模一样，包括副标题和符号。若一致，请继续申请。',
  aiNameTip:
    '注意：AI生成作品暂不支持登记，请检查样本中是否包含AI水印，以及去掉名称中的“AI”字样。若作品名称不是表达AI生成的意思，请继续申请。',
  aiKeywords: ['AI', 'ai', 'Ai', '生成', '人工智能'],
  riskTitle: '⚠️审核红线清单',
  riskIntro:
    '本提醒基于目前数登补正较多的内容。若因未阅读本提醒导致需要重做DCI或者数登审核不通过的，本平台不承担任何责任。',
  manualUrl: 'https://www.kdocs.cn/l/cvbV9ITu3mW6',
  manualLabel: '操作手册',
  riskItems: [
    '商标：如需包含®、™标识，需提供图样一致的商标证（含底纹、字体）。',
    '非遗：涉及非遗、中华老字号等字样的，需提供相应证明，且在著作权人名下。',
    '信息：样本中不得出现电话、二维码、第三方机构名称，或非著作权人的地址等信息。',
    'AI：禁止出现AI、生成字样；尤其注意右下角水印。',
    '清晰：图片类作品，要求所有内容均清晰可见，包括小字。',
    '字体：纯文字组成的美术作品需字体具备独创性，通用字体不予登记。',
    '元素：【立体五角星】【麦穗图案】【国旗图案】【伟人名称】【党政】【国家相关部门标识与元素】等，非相关机构均不可使用。',
    '查重：数登会全网检索查重；对知名商标、图案、人物形象均会查重。登记时确保不存在抄袭和大比例借鉴。'
  ],
  ownershipTemplates: [
    {
      ownershipWay: 'ENTERPRISE',
      label: '法人创作说明',
      file: 'enterprise-creation.doc',
      downloadName: '法人创作说明.doc'
    }
  ],
  otherProofTemplates: [
    { label: '上级法人单位声明', file: 'parent-legal-statement.docx', downloadName: '上级法人单位声明.docx' },
    { label: '不主张权利声明', file: 'no-claim-statement.docx', downloadName: '不主张权利声明.docx' },
    { label: '商标授权使用声明', file: 'trademark-auth.doc', downloadName: '商标授权使用声明.doc' },
    { label: '声音授权文件', file: 'voice-auth.docx', downloadName: '声音授权文件.docx' },
    { label: '委托创作说明', file: 'commission-creation.doc', downloadName: '委托创作说明.doc' },
    { label: '监护人知悉函', file: 'guardian-notice.docx', downloadName: '监护人知悉函.docx' },
    { label: '职务创作说明', file: 'employment-creation.doc', downloadName: '职务创作说明.doc' },
    { label: '肖像使用授权书', file: 'portrait-auth.docx', downloadName: '肖像使用授权书.docx' }
  ]
}

let tipsCache = null
let isLoaded = false

function mergeTips (remote) {
  if (!remote || typeof remote !== 'object') {
    return { ...DEFAULT_REGISTRATION_TIPS }
  }
  return {
    ...DEFAULT_REGISTRATION_TIPS,
    ...remote,
    aiKeywords:
      Array.isArray(remote.aiKeywords) && remote.aiKeywords.length
        ? remote.aiKeywords
        : DEFAULT_REGISTRATION_TIPS.aiKeywords,
    riskItems:
      Array.isArray(remote.riskItems) && remote.riskItems.length
        ? remote.riskItems
        : DEFAULT_REGISTRATION_TIPS.riskItems,
    ownershipTemplates:
      Array.isArray(remote.ownershipTemplates) && remote.ownershipTemplates.length
        ? remote.ownershipTemplates
        : DEFAULT_REGISTRATION_TIPS.ownershipTemplates,
    otherProofTemplates:
      Array.isArray(remote.otherProofTemplates) && remote.otherProofTemplates.length
        ? remote.otherProofTemplates
        : DEFAULT_REGISTRATION_TIPS.otherProofTemplates
  }
}

/**
 * 获取提示配置（同步，需先 loadRegistrationTips）
 */
export function getRegistrationTips () {
  return tipsCache || { ...DEFAULT_REGISTRATION_TIPS }
}

/**
 * 异步加载提示配置；失败时回退默认文案
 */
export async function loadRegistrationTips () {
  if (isLoaded) {
    return getRegistrationTips()
  }

  try {
    const response = await fetch('/config/registration-tips.json')
    if (response.ok) {
      tipsCache = mergeTips(await response.json())
    } else {
      console.warn('[registrationTipsLoader] 配置加载失败，使用默认文案')
      tipsCache = { ...DEFAULT_REGISTRATION_TIPS }
    }
  } catch (error) {
    console.warn('[registrationTipsLoader] 配置不存在或加载错误，使用默认文案')
    tipsCache = { ...DEFAULT_REGISTRATION_TIPS }
  }

  isLoaded = true
  return getRegistrationTips()
}

/**
 * 作品名称是否命中 AI 相关关键词（仅用于展示提示，不拦截提交）
 */
export function matchAiNameKeyword (workName, keywords) {
  const name = typeof workName === 'string' ? workName : ''
  if (!name) return false
  const list =
    Array.isArray(keywords) && keywords.length
      ? keywords
      : DEFAULT_REGISTRATION_TIPS.aiKeywords
  return list.some(keyword => keyword && name.includes(keyword))
}

/**
 * 证明模板下载地址：/templates + 英文文件名，避免中文路径被 nginx 回首页
 */
export function getTemplateDownloadUrl (fileName) {
  if (!fileName) return ''
  return `/templates/${fileName}`
}

/**
 * 拉取静态模板并触发本地下载。
 * 若服务端回了首页 HTML（常见于路径未命中静态文件），则不保存。
 */
export async function downloadTemplateFile (fileName, saveAsName) {
  const url = getTemplateDownloadUrl(fileName)
  if (!url) {
    throw new Error('模板文件名无效')
  }
  const response = await fetch(url, { credentials: 'same-origin' })
  if (!response.ok) {
    throw new Error('模板文件不存在或下载失败')
  }
  const contentType = (response.headers.get('content-type') || '').toLowerCase()
  const blob = await response.blob()
  if (contentType.includes('text/html') || blob.type.includes('text/html')) {
    throw new Error('模板未正确部署，请联系管理员')
  }
  const headText = await blob.slice(0, 80).text()
  if (
    headText.indexOf('<!DOCTYPE') !== -1 ||
    headText.indexOf('<html') !== -1 ||
    headText.indexOf("doesn't work properly without JavaScript") !== -1
  ) {
    throw new Error('模板未正确部署，请联系管理员')
  }
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = saveAsName || fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(objectUrl)
}
